package dev.lingvo.site.api

import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.RenderCheckReportDto
import dev.lingvo.shared.RenderDto
import dev.lingvo.shared.RenderFitDto
import dev.lingvo.shared.ShotExportKind
import dev.lingvo.shared.ShotExportLayout
import dev.lingvo.shared.ShotPushJobDto
import dev.lingvo.shared.ShotPushPreflightDto
import dev.lingvo.shared.ShotPushRequest
import dev.lingvo.shared.ShotSetApprovalDto
import dev.lingvo.shared.ShotSetApprovalRequest
import dev.lingvo.shared.ShotStoreImportDto
import dev.lingvo.shared.ShotStoreImportRequest
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.StoreShotsDto
import kotlinx.serialization.decodeFromString
import org.w3c.files.Blob

/** Builds the raw render path with all provenance needed by server-side eligibility checks. */
fun ApiClient.screenshotRenderUploadPath(
    slug: String, platform: StorePlatform, slotId: String, device: DeviceClass, locale: String,
    designVersion: Int, fit: RenderFitDto, captureId: String? = null, captureSha256: String? = null, captionSha256: String? = null,
): String =
    shipBase(slug, platform) + "/slots/${segment(slotId)}/renders/${segment(device.id)}/${segment(locale)}" +
        query("designVersion" to designVersion.toString(), "fontPx" to fit.fontPx.toString(), "floorPx" to fit.floorPx.toString(),
            "overflow" to fit.overflow.toString(), "lines" to fit.lines.toString(), "captureId" to captureId,
            "captureSha256" to captureSha256, "captionSha256" to captionSha256)

/** Uploads composed bytes with provenance and cancellable progress. */
suspend fun ApiClient.putScreenshotRender(
    slug: String, platform: StorePlatform, slotId: String, device: DeviceClass, locale: String,
    designVersion: Int, fit: RenderFitDto, body: Blob, contentType: String,
    captureId: String? = null, captureSha256: String? = null, captionSha256: String? = null,
    upload: XhrUpload = XhrUpload(), onProgress: (Long, Long) -> Unit = { _, _ -> },
): RenderDto =
    upload.put(screenshotRenderUploadPath(slug, platform, slotId, device, locale, designVersion, fit, captureId, captureSha256, captionSha256),
        body, contentType, onProgress = onProgress) { API_JSON.decodeFromString<RenderDto>(it) }

/** Lists render metadata independently of image bytes. */
suspend fun ApiClient.screenshotRenders(slug: String, platform: StorePlatform, locale: String? = null): List<RenderDto> =
    jsonRequest("GET", shipBase(slug, platform) + "/renders" + query("locale" to locale))

/** Uses authenticated fetch for a private render preview. */
suspend fun ApiClient.screenshotRenderImage(slug: String, platform: StorePlatform, renderId: String): Blob =
    blobRequest("GET", shipBase(slug, platform) + "/renders/${segment(renderId)}/image", "image/png, image/jpeg")

/** Deletes an explicit render through the session-only server route. */
suspend fun ApiClient.deleteScreenshotRender(slug: String, platform: StorePlatform, renderId: String): Unit =
    unitRequest("DELETE", shipBase(slug, platform) + "/renders/${segment(renderId)}")

/** Checks eligibility before approving or exporting composed images. */
suspend fun ApiClient.checkScreenshotRenders(slug: String, platform: StorePlatform): RenderCheckReportDto =
    jsonRequest("GET", shipBase(slug, platform) + "/renders/check")

/** Approves exactly the image identities and hashes the reviewer saw. */
suspend fun ApiClient.approveScreenshotSet(slug: String, platform: StorePlatform, request: ShotSetApprovalRequest): ShotSetApprovalDto =
    jsonRequest("POST", shipBase(slug, platform) + "/approvals", request)

/** Loads the newest unrevoked approval with validity recomputed by the server. */
suspend fun ApiClient.currentScreenshotApproval(slug: String, platform: StorePlatform): ShotSetApprovalDto =
    jsonRequest("GET", shipBase(slug, platform) + "/approvals/current")

/** Revokes the explicit approval without silently approving a newer set. */
suspend fun ApiClient.revokeScreenshotApproval(slug: String, platform: StorePlatform, approvalId: String): Unit =
    unitRequest("POST", shipBase(slug, platform) + "/approvals/${segment(approvalId)}/revoke")

/** Preflight reads the same selected approval scope the push will consume. */
suspend fun ApiClient.screenshotPushPreflight(
    slug: String, platform: StorePlatform, approvalId: String, locales: List<String> = emptyList(), devices: List<DeviceClass> = emptyList(),
): ShotPushPreflightDto =
    jsonRequest("GET", shipBase(slug, platform) + "/push/preflight" + query("approvalId" to approvalId,
        "locales" to locales.takeIf { it.isNotEmpty() }?.joinToString(","),
        "devices" to devices.takeIf { it.isNotEmpty() }?.joinToString(",") { it.id }))

/** Starts a durable push under the current person's session. */
suspend fun ApiClient.pushScreenshotSet(slug: String, platform: StorePlatform, request: ShotPushRequest): ShotPushJobDto =
    jsonRequest("POST", shipBase(slug, platform) + "/push", request)

/** Lists the server's bounded recent job history. */
suspend fun ApiClient.screenshotPushJobs(slug: String, platform: StorePlatform): List<ShotPushJobDto> =
    jsonRequest("GET", shipBase(slug, platform) + "/push/jobs")

/** Polls a durable job without retaining its original connection. */
suspend fun ApiClient.screenshotPushJob(slug: String, platform: StorePlatform, jobId: String): ShotPushJobDto =
    jsonRequest("GET", shipBase(slug, platform) + "/push/jobs/${segment(jobId)}")

/** Lets the server decide whether the current job state is cancellable. */
suspend fun ApiClient.cancelScreenshotPush(slug: String, platform: StorePlatform, jobId: String): ShotPushJobDto =
    jsonRequest("POST", shipBase(slug, platform) + "/push/jobs/${segment(jobId)}/cancel")

/** Rechecks a waiting job against the current store version. */
suspend fun ApiClient.retryScreenshotPush(slug: String, platform: StorePlatform, jobId: String): ShotPushJobDto =
    jsonRequest("POST", shipBase(slug, platform) + "/push/jobs/${segment(jobId)}/retry")

/** Reads the store's current listing images under the person's session. */
suspend fun ApiClient.screenshotStoreImages(
    slug: String, platform: StorePlatform, locales: List<String> = emptyList(), devices: List<DeviceClass> = emptyList(),
): StoreShotsDto =
    jsonRequest("GET", shipBase(slug, platform) + "/store-images" + query(
        "locales" to locales.takeIf { it.isNotEmpty() }?.joinToString(","),
        "devices" to devices.takeIf { it.isNotEmpty() }?.joinToString(",") { it.id }))

/** Imports explicit store selections as captures without silently creating plan slots. */
suspend fun ApiClient.importScreenshotStoreImages(slug: String, platform: StorePlatform, request: ShotStoreImportRequest): ShotStoreImportDto =
    jsonRequest("POST", shipBase(slug, platform) + "/store-images/import", request)

/** Builds the credential-free endpoint; use [screenshotExport] to obtain bytes for a download link. */
fun ApiClient.exportUrl(
    slug: String, platform: StorePlatform, kind: ShotExportKind, layout: ShotExportLayout,
    locales: List<String> = emptyList(), devices: List<DeviceClass> = emptyList(), slots: List<String> = emptyList(),
): String =
    ApiConfig.baseUrl + shipBase(slug, platform) + "/export" + query("kind" to kind.name.lowercase(),
        "layout" to layout.name.lowercase(), "locales" to locales.takeIf { it.isNotEmpty() }?.joinToString(","),
        "devices" to devices.takeIf { it.isNotEmpty() }?.joinToString(",") { it.id },
        "slots" to slots.takeIf { it.isNotEmpty() }?.joinToString(","))

/** Fetches a private ZIP with the session bearer; callers own and revoke any Blob URL used for a link. */
suspend fun ApiClient.screenshotExport(
    slug: String, platform: StorePlatform, kind: ShotExportKind, layout: ShotExportLayout,
    locales: List<String> = emptyList(), devices: List<DeviceClass> = emptyList(), slots: List<String> = emptyList(),
): Blob = blobRequest("GET", exportUrl(slug, platform, kind, layout, locales, devices, slots).removePrefix(ApiConfig.baseUrl), ZIP_CONTENT_TYPE)

private fun shipBase(slug: String, platform: StorePlatform): String =
    "/projects/${segment(slug)}/screenshots/${segment(platform.id)}"
